import { ChevronRight } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavArea } from '../navigation.ts';

/**
 * Um departamento com as telas flutuando ao lado (submenu `flyout`): abre ao passar o mouse ou
 * com o foco do teclado, num painel preso à tela (a lista do menu rola e cortaria o painel).
 */
export function Flyout({
  area,
  current,
  children,
}: {
  area: NavArea;
  current: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const row = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = () => {
    window.clearTimeout(timer.current);
    const r = row.current?.getBoundingClientRect();
    if (r) setPos({ top: r.top, left: r.right + 6 });
  };
  const hide = () => {
    timer.current = window.setTimeout(() => setPos(null), 150);
  };
  const Icon = area.icon;
  return (
    <div
      ref={row}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) hide();
      }}
    >
      <button
        type="button"
        aria-expanded={pos !== null}
        onClick={() => (pos ? setPos(null) : show())}
        className={`flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm ${
          current ? 'font-medium' : 'text-ink hover:bg-paper'
        }`}
      >
        <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
        <span className="flex-1">{t(area.key)}</span>
        <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
      </button>
      {pos && (
        <div
          className="ui-sidebar ui-panel fixed z-50 flex w-60 flex-col gap-0.5 rounded-lg p-2"
          style={{ top: pos.top, left: pos.left }}
        >
          <span className="px-2.5 pt-1 pb-2 text-xs font-medium text-muted">{t(area.key)}</span>
          {children}
        </div>
      )}
    </div>
  );
}
